import { formatUnits } from "viem";
import { getConfig } from "../constants/config";
import tokenService from "../../common/tokenService";

/**
 * Calculates the total output amount across all paths in a route.
 *
 * @param {Array} route - Route array from getRoute
 *   Each path in the route should contain swap steps with tokenIn, tokenOut, and amountOutMin
 *
 * @returns {Promise<Object>} Route output details
 * @returns {bigint} .amountOut - Total output amount in raw units
 * @returns {string} .amountOutFormatted - Total output amount formatted with token decimals
 * @returns {string} .tokenIn - Input token address
 * @returns {string} .tokenOut - Output token address
 *
 * @throws {Error} If route is invalid or missing
 *
 * @example
 * // Example 1: Get total output for a single-path route
 * const output = await getTotalAmountOut(route);
 * console.log(output.amountOutFormatted); // "1.234567"
 *
 *
 * @note
 * - Returns undefined values if route is invalid
 * - Sums amountOutMin from each path for total output
 * - Uses token decimals from config for formatting
 */
export const getTotalAmountOut = async (route) => {
  if (!route || !Array.isArray(route)) {
    console.error("Invalid or missing `route`: Must be an array.");
    return {
      amountOut: undefined,
      amountOutFormatted: undefined,
      tokenIn: undefined,
      tokenOut: undefined,
    };
  }

  // Get config for non-token data
  const config = getConfig();
  const { chainId, additionalTokens, overrideDefaultTokens, nativeAddress } =
    config;

  // Get updated token list with user's configuration applied
  const tokens = await tokenService.getAsyncTokenList(
    chainId,
    additionalTokens,
    overrideDefaultTokens,
    nativeAddress
  );

  const tokenIn = tokens[route[0][0].tokenIn];
  const tokenOut = tokens[route[0].at(-1).tokenOut];

  let totalAmountOut = 0n;

  for (let i = 0; i < route.length; i++) {
    totalAmountOut = totalAmountOut + BigInt(route[i].at(-1)?.amountOutMin);
  }

  return {
    amountOut: totalAmountOut,
    amountOutFormatted: formatUnits(
      totalAmountOut,
      tokenOut.attributes.decimals
    ),
    tokenIn: tokenIn.attributes.address,
    tokenOut: tokenOut.attributes.address,
  };
};
